import React, { useState } from 'react';
import { X, User, Lock, ArrowRight } from 'lucide-react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from './firebase';

const LoginModal = ({ onClose, lang, t }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // ... imports etc are fine, just update the component body

    const handleSubmit = async (e) => {
        // ... same logic ...
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                await createUserWithEmailAndPassword(auth, email, password);
            }
            onClose(); // Close modal on success
        } catch (err) {
            console.error(err);
            let msg = 'Authentication failed.';
            // Keeping raw error messages for now or could genericize
            if (err.code === 'auth/invalid-email') msg = 'Invalid email address.';
            if (err.code === 'auth/user-not-found') msg = 'User not found.';
            if (err.code === 'auth/wrong-password') msg = 'Incorrect password.';
            if (err.code === 'auth/email-already-in-use') msg = 'Email already in use.';
            if (err.code === 'auth/weak-password') msg = 'Password should be at least 6 characters.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError(null);
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            onClose();
        } catch (err) {
            console.error(err);
            setError("Google authentication failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal Card */}
            <div className="relative bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-md">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 bg-red-500 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-none transition-all"
                >
                    <X size={20} className="text-white" strokeWidth={3} />
                </button>

                {/* Header */}
                <h2 className="text-3xl font-black uppercase mb-2">
                    {isLogin ? (t?.loginWelcome || 'Welcome Back') : (t?.signupWelcome || 'Join the Club')}
                </h2>
                <p className="font-bold text-gray-500 mb-8">
                    {isLogin ? (t?.loginDesc || 'Login to access admin features.') : (t?.signupDesc || 'Create an account to get started.')}
                </p>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">

                    {/* Email Input */}
                    <div className="relative">
                        <div className="absolute top-1/2 left-4 -translate-y-1/2 pointer-events-none">
                            <User size={24} />
                        </div>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t?.emailPlaceholder || "Email"}
                            className="w-full h-14 pl-12 pr-4 font-bold border-4 border-black bg-[#f0f0f0] focus:bg-white focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
                        />
                    </div>

                    {/* Password Input */}
                    <div className="relative">
                        <div className="absolute top-1/2 left-4 -translate-y-1/2 pointer-events-none">
                            <Lock size={24} />
                        </div>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t?.passwordPlaceholder || "Password"}
                            className="w-full h-14 pl-12 pr-4 font-bold border-4 border-black bg-[#f0f0f0] focus:bg-white focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
                        />
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-4 bg-red-100 border-2 border-black font-bold text-red-600">
                            {error}
                        </div>
                    )}

                    {/* Action Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="h-14 bg-[#8c52ff] text-white text-xl font-black border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (t?.waiting || 'WAITING...') : (isLogin ? (t?.loginBtn || 'LOGIN') : (t?.signupBtn || 'SIGN UP'))}
                        {!loading && <ArrowRight size={24} strokeWidth={3} />}
                    </button>
                </form>

                {/* Google Login Button */}
                <button
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="mt-4 w-full h-14 bg-white text-black text-lg font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-6 h-6" />
                    {isLogin ? (t?.googleLogin || 'Login with Google') : (t?.googleSignup || 'Sign up with Google')}
                </button>

                {/* Toggle Mode */}
                <div className="mt-8 text-center">
                    <button
                        onClick={() => setIsLogin(!isLogin)}
                        className="font-black underline decoration-2 underline-offset-4 hover:text-[#8c52ff]"
                    >
                        {isLogin ? (t?.needAccount || 'Need an account? Sign Up') : (t?.hasAccount || 'Already have an account? Login')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default LoginModal;
